import React from "react";
import { useLeave, useExcursion } from "contexts/api";
import { extractDataFromResponse } from "utils/paginationHelpers";
import { getStartOfWeek, getEndOfWeek, toLocalDateKey } from "utils/dateTime";
import type { LeaveRequest } from "api/leave/Leave.typed";
import type { ExcursionBooking } from "api/excursions/Excursion.typed";
import type { User } from "api/auth/Auth.typed";
import type { PlanningCalendarViewMode } from "./usePlanningCalendar";

export function usePlanningData(
  canAccessPlanning: boolean,
  user: User | null | undefined,
  viewMode: PlanningCalendarViewMode,
  currentDate: Date,
  shouldLoadExcursionBookings: boolean,
) {
  const { getLeaveRequests } = useLeave();
  const { listExcursionBookings } = useExcursion();

  const [requests, setRequests] = React.useState<LeaveRequest[]>([]);
  const [bookings, setBookings] = React.useState<ExcursionBooking[]>([]);
  const [loading, setLoading] = React.useState(true);

  const getLeaveRequestsRef = React.useRef(getLeaveRequests);
  getLeaveRequestsRef.current = getLeaveRequests;

  const listExcursionBookingsRef = React.useRef(listExcursionBookings);
  listExcursionBookingsRef.current = listExcursionBookings;

  const hasUser = user != null;

  const loadPlanningData = React.useCallback(async () => {
    if (!canAccessPlanning || !hasUser) {
      setRequests([]);
      setBookings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const startOfPeriod =
      viewMode === "month"
        ? new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        : getStartOfWeek(currentDate);
    const endOfPeriod =
      viewMode === "month"
        ? new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        : getEndOfWeek(currentDate);
    const fromKey = toLocalDateKey(startOfPeriod);
    const toKey = toLocalDateKey(endOfPeriod);
    const leaveQuery: Parameters<typeof getLeaveRequests>[0] = {
      From: fromKey,
      To: toKey,
    };

    const [response, bookingsData] = await Promise.all([
      getLeaveRequestsRef.current(leaveQuery),
      shouldLoadExcursionBookings
        ? listExcursionBookingsRef
            .current({
              from: fromKey,
              to: toKey,
            })
            .catch(() => [])
        : Promise.resolve([]),
    ]);
    const requestsData = extractDataFromResponse<LeaveRequest>(response);
    setRequests(requestsData);
    setBookings(bookingsData ?? []);
    setLoading(false);
  }, [canAccessPlanning, hasUser, currentDate, shouldLoadExcursionBookings, viewMode]);

  React.useEffect(() => {
    void loadPlanningData();
  }, [loadPlanningData]);

  return { requests, bookings, loading, loadPlanningData };
}
